/**
 * Columns hidden from the temp PGS / temp TGS review "acknowledgement" tables.
 *
 * These interim tables carry many columns StatCrew never populates at the review
 * (pre-integration) stage — Genius integration IDs assigned later, plus unmapped
 * metadata. We hide them so the operator sees only the columns StatCrew actually
 * fills (player/team identity + the stat `s_*` columns).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW TO EDIT (developers)
 *   • To HIDE one more column:  add its exact lowercase name to HIDDEN_COLUMN_NAMES.
 *   • To HIDE a whole family:    add a lowercase prefix to HIDDEN_COLUMN_PREFIXES
 *                                (e.g. 'g_' hides g_team_id, g_person_id, …).
 *   • To SHOW a hidden column:   delete it from the list (or remove the prefix).
 * Names are matched case-insensitively. This is a denylist, so anything NOT listed
 * here is always shown — you can never accidentally hide a populated stat column.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Hide any column whose lowercased name starts with one of these prefixes. */
export const HIDDEN_COLUMN_PREFIXES: readonly string[] = [
  // Genius integration IDs (g_team_id, g_person_id, g_competition_id, g_pool_*,
  // g_round_*, g_division_id, g_conference_id, g_opponent_*, g_tournament_id …).
  // Assigned during integration, so always null in the review temp tables.
  // Safe: no StatCrew identity/stat column starts with 'g_' — 'game_date' and
  // 'game_result' do not match because the char after 'g' must be '_'.
  'g_',
];

/** Hide these exact columns (compared lowercased). Grouped by reason for clarity. */
export const HIDDEN_COLUMN_NAMES: ReadonlySet<string> = new Set<string>([
  // Internal row bookkeeping
  'id',
  'stats_that_do_not_match',
  'usable_periods',

  // Unmapped competition / division / tournament metadata
  'competition_name',
  'division_name',
  'opponent_division_name',
  'tournament_name',
  'match_status',

  // Presentation-only metadata StatCrew doesn't provide
  'team_logo',
  'opponent_logo',
  'team_nickname',
  'opponent_team_nickname',
  'team_nickname_international',
  'opponent_team_nickname_international',
  'time_zone',
]);

/** True when `col` should be hidden from the review PGS/TGS tables. */
export function isHiddenReviewColumn(col: string): boolean {
  const c = col.toLowerCase();
  if (HIDDEN_COLUMN_PREFIXES.some((prefix) => c.startsWith(prefix))) return true;
  return HIDDEN_COLUMN_NAMES.has(c);
}
